import type {PagePromptContent} from '../store/thread';

type ContentType = PagePromptContent['type'];
type ContributionId = number;
type StoredSlice = Partial<Omit<PagePromptContent, 'type'>>;
type SliceGetter = () => StoredSlice | null;

interface Contribution {
  type: ContentType;
  getSlice: SliceGetter;
}

export interface PageContentStore {
  set<V extends PagePromptContent>(
    id: ContributionId,
    type: V['type'],
    getSlice: () => Partial<Omit<V, 'type'>> | null
  ): void;
  remove(id: ContributionId): void;
  collect(): PagePromptContent[];
}

export const pageContentStore: PageContentStore = createPageContentStore();

/** createPageContentStore builds a new `PageContentStore` instance. */
export function createPageContentStore(): PageContentStore {
  const contributions = new Map<ContributionId, Contribution>();

  return {
    set(id, type, getSlice) {
      contributions.set(id, {type, getSlice: getSlice as SliceGetter});
    },

    remove(id) {
      contributions.delete(id);
    },

    collect() {
      const buckets = new Map<
        ContentType,
        {type: ContentType} & Record<string, unknown>
      >();
      for (const {type, getSlice} of contributions.values()) {
        const slice = getSlice();
        if (slice == null) {
          continue;
        }

        let bucket = buckets.get(type);
        if (bucket == null) {
          bucket = {type};
          buckets.set(type, bucket);
        }

        // TODO: Object.assign is last-write-wins per field. Array-valued slice
        // fields will overwrite, not concatenate. Add a per-field merge
        // strategy when the first array-valued field lands.
        Object.assign(bucket, slice);
      }
      return Array.from(buckets.values()) as PagePromptContent[];
    },
  };
}
